import type { Ref } from 'vue'
import type {
  Article,
  Recipe,
  ShoppingCartItem,
  Unit,
  ArticleCategory,
  RecipeCategory
} from '../data/mockData'
import { parseAmountAndUnit } from '../utils/amountParser'

export interface EntityReplacementContext {
  articles: Ref<Article[]>
  recipes: Ref<Recipe[]>
  shoppingList: Ref<ShoppingCartItem[]>
  units: Ref<Unit[]>
  articleCategories: Ref<ArticleCategory[]>
  recipeCategories: Ref<RecipeCategory[]>
  deleteWithReplacementState: Ref<any>
  updateRecipe: (recipe: Recipe) => Promise<void>
  updateShoppingItem: (item: ShoppingCartItem) => Promise<void>
  updateArticle: (article: Article) => Promise<void>
  deleteArticle: (id: string, silent?: boolean, customToast?: string) => Promise<void>
  deleteUnit: (name: string) => Promise<void>
  addUnit: (unit: Unit) => Promise<void>
  deleteArticleCategory: (id: string, customToast?: string) => Promise<void>
  deleteRecipeCategory: (id: string, customToast?: string) => Promise<void>
  confirmAction: (title: string, message: string, confirmLabel?: string, cancelLabel?: string) => Promise<boolean>
  showToast: (msg: string, type?: 'info' | 'success' | 'warning' | 'error', action?: { label: string; callback: () => void }) => void
}

export function createEntityReplacementService(ctx: EntityReplacementContext) {
  const {
    articles,
    recipes,
    shoppingList,
    units,
    articleCategories,
    recipeCategories,
    deleteWithReplacementState,
    updateRecipe,
    updateShoppingItem,
    updateArticle,
    deleteArticle,
    deleteUnit,
    addUnit,
    deleteArticleCategory,
    deleteRecipeCategory,
    confirmAction,
    showToast
  } = ctx

  const replaceAndDeleteArticle = async (oldArticleId: string, replacementArticleId?: string) => {
    const replacementArticle = replacementArticleId ? articles.value.find(a => a.id === replacementArticleId) : null

    if (replacementArticle) {
      // 1. Update recipes
      for (const recipe of recipes.value) {
        if (recipe.ingredients?.some(i => i.articleID === oldArticleId)) {
          const updatedIngredients = recipe.ingredients.map(ing => {
            if (ing.articleID === oldArticleId) {
              return { ...ing, articleID: replacementArticle.id }
            }
            return ing
          })
          await updateRecipe({
            ...recipe,
            ingredients: updatedIngredients
          })
        }
      }

      // 2. Update shopping list
      for (const item of shoppingList.value) {
        if (item.articleID === oldArticleId) {
          const category = articleCategories.value.find(c => c.id === replacementArticle.categoryID)
          await updateShoppingItem({
            ...item,
            articleID: replacementArticle.id,
            articleName: replacementArticle.name,
            categoryName: category?.name || item.categoryName || 'Sonstiges',
            categoryColor: category?.color || item.categoryColor || '200,200,200',
            articleImg: replacementArticle.img || ''
          })
        }
      }
    }

    // 3. Delete old article with single consolidated toast
    const customToast = replacementArticle
      ? `Artikel durch „${replacementArticle.name}“ ersetzt und gelöscht`
      : undefined
    await deleteArticle(oldArticleId, false, customToast)
  }

  const promptDeleteArticle = async (articleId: string, fallbackArticle?: { id: string; name: string }): Promise<boolean> => {
    const article = articles.value.find(a => a.id === articleId) || fallbackArticle
    if (!article) return false

    const affectedRecipes = recipes.value
      .filter(r => r.ingredients?.some(i => i.articleID === articleId))
      .map(r => ({ id: r.id, name: r.name }))

    const affectedShoppingItems = shoppingList.value
      .filter(s => s.articleID === articleId)
      .map(s => ({ id: s.id, articleName: s.articleName || '' }))

    if (affectedRecipes.length === 0 && affectedShoppingItems.length === 0) {
      const proceed = await confirmAction(
        'Artikel löschen?',
        `Möchtest du den Artikel "${article.name}" wirklich löschen?`,
        'Löschen',
        'Abbrechen'
      )
      if (proceed) {
        await deleteArticle(articleId)
        return true
      }
      return false
    }

    // References exist -> Show replacement dialog
    const choice = await new Promise<{ action: 'replace' | 'delete' | 'cancel'; replacementId?: string }>((resolve) => {
      deleteWithReplacementState.value = {
        isOpen: true,
        type: 'article',
        item: { id: article.id, name: article.name },
        affectedRecipes,
        affectedShoppingItems,
        affectedArticles: [],
        resolve
      }
    })

    if (choice.action === 'cancel') {
      return false
    }

    if (choice.action === 'replace' && choice.replacementId) {
      await replaceAndDeleteArticle(articleId, choice.replacementId)
      return true
    }

    if (choice.action === 'delete') {
      await deleteArticle(articleId)
      return true
    }

    return false
  }

  const replaceAndDeleteUnit = async (oldUnitName: string, replacementUnitName?: string) => {
    const unitCopy = units.value.find(u => u.name === oldUnitName)

    if (replacementUnitName) {
      // 1. Update articles
      for (const art of articles.value) {
        let changed = false
        const updatedArt = { ...art }
        if (updatedArt.unit === oldUnitName) { updatedArt.unit = replacementUnitName; changed = true }
        if (updatedArt.shoppingUnit === oldUnitName) { updatedArt.shoppingUnit = replacementUnitName; changed = true }
        if (updatedArt.recipeUnit === oldUnitName) { updatedArt.recipeUnit = replacementUnitName; changed = true }
        if (updatedArt.lastShoppingUnit === oldUnitName) { updatedArt.lastShoppingUnit = replacementUnitName; changed = true }
        if (updatedArt.lastRecipeUnit === oldUnitName) { updatedArt.lastRecipeUnit = replacementUnitName; changed = true }
        if (changed) {
          await updateArticle(updatedArt)
        }
      }

      // 2. Update recipes
      for (const recipe of recipes.value) {
        let recipeChanged = false
        const updatedIngredients = (recipe.ingredients || []).map(ing => {
          const { amount: rawAmt, unit: rawUnit } = parseAmountAndUnit(ing.amount)
          if (rawUnit.trim() === oldUnitName) {
            recipeChanged = true
            const newAmount = rawAmt ? `${rawAmt} ${replacementUnitName}` : replacementUnitName
            return { ...ing, amount: newAmount }
          }
          return ing
        })
        if (recipeChanged) {
          await updateRecipe({ ...recipe, ingredients: updatedIngredients })
        }
      }

      // 3. Update shopping list
      for (const item of shoppingList.value) {
        const { amount: rawAmt, unit: rawUnit } = parseAmountAndUnit(item.amount)
        if (rawUnit.trim() === oldUnitName) {
          const newAmount = rawAmt ? `${rawAmt} ${replacementUnitName}` : replacementUnitName
          await updateShoppingItem({ ...item, amount: newAmount })
        }
      }
    } else {
      // Clear unit references on articles
      for (const art of articles.value) {
        let changed = false
        const updatedArt = { ...art }
        if (updatedArt.unit === oldUnitName) { updatedArt.unit = ''; changed = true }
        if (updatedArt.shoppingUnit === oldUnitName) { updatedArt.shoppingUnit = ''; changed = true }
        if (updatedArt.recipeUnit === oldUnitName) { updatedArt.recipeUnit = ''; changed = true }
        if (updatedArt.lastShoppingUnit === oldUnitName) { updatedArt.lastShoppingUnit = undefined; changed = true }
        if (updatedArt.lastRecipeUnit === oldUnitName) { updatedArt.lastRecipeUnit = undefined; changed = true }
        if (changed) {
          await updateArticle(updatedArt)
        }
      }
    }

    // 4. Delete unit
    await deleteUnit(oldUnitName)
    if (replacementUnitName) {
      showToast(`Einheit „${oldUnitName}“ durch „${replacementUnitName}“ ersetzt und gelöscht`, 'success', {
        label: 'Rückgängig',
        callback: async () => {
          if (unitCopy) {
            await addUnit(unitCopy)
            showToast(`Einheit „${oldUnitName}“ wiederhergestellt`, 'success')
          }
        }
      })
    } else {
      showToast(`Einheit „${oldUnitName}“ gelöscht`, 'success', {
        label: 'Rückgängig',
        callback: async () => {
          if (unitCopy) {
            await addUnit(unitCopy)
            showToast(`Einheit „${oldUnitName}“ wiederhergestellt`, 'success')
          }
        }
      })
    }
  }

  const promptDeleteUnit = async (unitName: string): Promise<boolean> => {
    const unit = units.value.find(u => u.name === unitName)
    if (!unit) return false

    const affectedArticles = articles.value
      .filter(a => a.unit === unitName || a.shoppingUnit === unitName || a.recipeUnit === unitName || a.lastShoppingUnit === unitName || a.lastRecipeUnit === unitName)
      .map(a => ({ id: a.id, name: a.name }))

    const affectedRecipes = recipes.value
      .filter(r => r.ingredients?.some(i => parseAmountAndUnit(i.amount).unit === unitName))
      .map(r => ({ id: r.id, name: r.name }))

    if (affectedArticles.length === 0 && affectedRecipes.length === 0) {
      const proceed = await confirmAction(
        'Einheit löschen?',
        `Möchtest du die Einheit "${unitName}" wirklich löschen?`,
        'Löschen',
        'Abbrechen'
      )
      if (proceed) {
        const unitCopy = { ...unit }
        await deleteUnit(unitName)
        showToast(`Einheit „${unitName}“ gelöscht`, 'success', {
          label: 'Rückgängig',
          callback: async () => {
            await addUnit(unitCopy)
            showToast(`Einheit „${unitName}“ wiederhergestellt`, 'success')
          }
        })
        return true
      }
      return false
    }

    // References exist -> Show replacement dialog
    const choice = await new Promise<{ action: 'replace' | 'delete' | 'cancel'; replacementId?: string }>((resolve) => {
      deleteWithReplacementState.value = {
        isOpen: true,
        type: 'unit',
        item: { id: unitName, name: unitName },
        affectedArticles,
        affectedRecipes,
        affectedShoppingItems: [],
        resolve
      }
    })

    if (choice.action === 'cancel') {
      return false
    }

    if (choice.action === 'replace' && choice.replacementId) {
      await replaceAndDeleteUnit(unitName, choice.replacementId)
      return true
    }

    if (choice.action === 'delete') {
      await replaceAndDeleteUnit(unitName, undefined)
      return true
    }

    return false
  }

  const replaceAndDeleteArticleCategory = async (oldCategoryId: string, replacementCategoryId?: string) => {
    const replacementCategory = replacementCategoryId ? articleCategories.value.find(c => c.id === replacementCategoryId) : null

    if (replacementCategory) {
      for (const art of articles.value) {
        if (art.categoryID === oldCategoryId) {
          await updateArticle({
            ...art,
            categoryID: replacementCategory.id
          })
        }
      }

      for (const item of shoppingList.value) {
        const art = articles.value.find(a => a.id === item.articleID)
        if (art && art.categoryID === replacementCategory.id) {
          await updateShoppingItem({
            ...item,
            categoryName: replacementCategory.name,
            categoryColor: replacementCategory.color
          })
        }
      }
    }

    const customToast = replacementCategory
      ? `Artikel in Kategorie „${replacementCategory.name}“ verschoben und gelöscht`
      : undefined
    await deleteArticleCategory(oldCategoryId, customToast)
  }

  const promptDeleteArticleCategory = async (categoryId: string, fallbackCategory?: { id: string; name: string }): Promise<boolean> => {
    const category = articleCategories.value.find(c => c.id === categoryId) || fallbackCategory
    if (!category) return false

    const affectedArticles = articles.value
      .filter(a => a.categoryID === categoryId)
      .map(a => ({ id: a.id, name: a.name }))

    if (affectedArticles.length === 0) {
      const proceed = await confirmAction(
        'Kategorie löschen?',
        `Möchtest du die Kategorie "${category.name}" wirklich löschen?`,
        'Löschen',
        'Abbrechen'
      )
      if (proceed) {
        await deleteArticleCategory(categoryId)
        return true
      }
      return false
    }

    const choice = await new Promise<{ action: 'replace' | 'delete' | 'cancel'; replacementId?: string }>((resolve) => {
      deleteWithReplacementState.value = {
        isOpen: true,
        type: 'articleCategory',
        item: { id: category.id, name: category.name },
        affectedArticles,
        affectedRecipes: [],
        affectedShoppingItems: [],
        resolve
      }
    })

    if (choice.action === 'cancel') {
      return false
    }

    if (choice.action === 'replace' && choice.replacementId) {
      await replaceAndDeleteArticleCategory(categoryId, choice.replacementId)
      return true
    }

    if (choice.action === 'delete') {
      await deleteArticleCategory(categoryId)
      return true
    }

    return false
  }

  const replaceAndDeleteRecipeCategory = async (oldCategoryId: string, replacementCategoryId?: string) => {
    const replacementCategory = replacementCategoryId ? recipeCategories.value.find(c => c.id === replacementCategoryId) : null

    if (replacementCategory) {
      for (const rec of recipes.value) {
        let changed = false
        let newCatId = rec.categoryID
        let newCatIds = rec.categoryIDs ? [...rec.categoryIDs] : []

        if (newCatId === oldCategoryId) {
          newCatId = replacementCategory.id
          changed = true
        }

        if (newCatIds.includes(oldCategoryId)) {
          newCatIds = newCatIds.map(id => id === oldCategoryId ? replacementCategory.id : id)
          newCatIds = Array.from(new Set(newCatIds))
          changed = true
        }

        if (changed) {
          await updateRecipe({
            ...rec,
            categoryID: newCatId,
            categoryIDs: newCatIds
          })
        }
      }
    }

    const customToast = replacementCategory
      ? `Rezepte in Kategorie „${replacementCategory.name}“ verschoben und gelöscht`
      : undefined
    await deleteRecipeCategory(oldCategoryId, customToast)
  }

  const promptDeleteRecipeCategory = async (categoryId: string, fallbackCategory?: { id: string; name: string }): Promise<boolean> => {
    const category = recipeCategories.value.find(c => c.id === categoryId) || fallbackCategory
    if (!category) return false

    const affectedRecipes = recipes.value
      .filter(r => r.categoryID === categoryId || r.categoryIDs?.includes(categoryId))
      .map(r => ({ id: r.id, name: r.name }))

    if (affectedRecipes.length === 0) {
      const proceed = await confirmAction(
        'Kategorie löschen?',
        `Möchtest du die Kategorie "${category.name}" wirklich löschen?`,
        'Löschen',
        'Abbrechen'
      )
      if (proceed) {
        await deleteRecipeCategory(categoryId)
        return true
      }
      return false
    }

    const choice = await new Promise<{ action: 'replace' | 'delete' | 'cancel'; replacementId?: string }>((resolve) => {
      deleteWithReplacementState.value = {
        isOpen: true,
        type: 'recipeCategory',
        item: { id: category.id, name: category.name },
        affectedRecipes,
        affectedArticles: [],
        affectedShoppingItems: [],
        resolve
      }
    })

    if (choice.action === 'cancel') {
      return false
    }

    if (choice.action === 'replace' && choice.replacementId) {
      await replaceAndDeleteRecipeCategory(categoryId, choice.replacementId)
      return true
    }

    if (choice.action === 'delete') {
      await deleteRecipeCategory(categoryId)
      return true
    }

    return false
  }

  return {
    replaceAndDeleteArticle,
    promptDeleteArticle,
    replaceAndDeleteUnit,
    promptDeleteUnit,
    replaceAndDeleteArticleCategory,
    promptDeleteArticleCategory,
    replaceAndDeleteRecipeCategory,
    promptDeleteRecipeCategory
  }
}
